/**
 * CAP-API-1a-1 — consume ``notification_group_slots`` from GET /dashboard/overview.
 */

/**
 * Align server ``owner_admin`` with legacy ``buildGroupSlotsFromDeviceList`` tenant prefix.
 * Grantee admins prefix ``shared_by`` (≈ owner when ``owner_admin !== me.username``).
 *
 * @param {{ owner_admin?: unknown }} row
 * @param {{ me?: object | null }} ctx
 * @returns {string}
 */
export function resolveOverviewSlotTenantOwner(row, ctx) {
  const me = ctx && ctx.me;
  const isSuper = !!(me && me.role === "superadmin");
  const oa = String((row && row.owner_admin) || "").trim();
  if (isSuper) return oa;
  const meName = String(me && me.username || "").trim();
  if (oa && meName && oa !== meName) return oa;
  return "";
}

/**
 * @param {{ serverCounts?: { total?: unknown } } | null | undefined} slot
 * @returns {number | null}
 */
function _slotServerTotal(slot) {
  if (!slot || !slot.serverCounts) return null;
  const t = Number(slot.serverCounts.total);
  return Number.isFinite(t) ? t : null;
}

/**
 * Drop ghost superadmin "Unassigned" cards when PG slots already bucket by owner (GS-UNIFY-1).
 *
 * @param {Array<object>} slots
 * @param {{ me?: object | null }} [ctx]
 * @returns {Array<object>}
 */
export function coalesceOverviewGroupSlots(slots, ctx = {}) {
  const list = Array.isArray(slots) ? slots.slice() : [];
  const isSuper = !!(ctx.me && ctx.me.role === "superadmin");
  if (!isSuper) return list;
  const byGk = new Map();
  for (const s of list) {
    const gk = String(s.groupKey || "");
    if (!gk) continue;
    if (!byGk.has(gk)) byGk.set(gk, []);
    byGk.get(gk).push(s);
  }
  const drop = new Set();
  for (const group of byGk.values()) {
    const owned = group.filter((s) => String(s.tenantOwner || "").trim());
    const bare = group.filter((s) => !String(s.tenantOwner || "").trim());
    if (!owned.length || !bare.length) continue;
    const ownedServer = owned.some((s) => (_slotServerTotal(s) || 0) > 0);
    if (!ownedServer) continue;
    for (const u of bare) {
      const uTotal = _slotServerTotal(u);
      if (uTotal == null || uTotal === 0) drop.add(String(u.metaKey || ""));
    }
  }
  return list.filter((s) => !drop.has(String(s.metaKey || "")));
}

/**
 * Infer ``tenantOwner`` for superadmin cards and preserve server display samples (UX-GS-DISPLAY-1).
 *
 * @param {Array<object>} slots
 * @param {object[]} devices
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @param {(devices: object[], gk: string, canonicalGroupKey: (v: unknown) => string) => string} inferUniqueOwnerForGroup
 * @returns {Array<object>}
 */
export function enrichOverviewSlotsForDisplay(slots, devices, ctx, inferUniqueOwnerForGroup) {
  const list = Array.isArray(slots) ? slots.slice() : [];
  const isSuper = !!(ctx.me && ctx.me.role === "superadmin");
  const devs = Array.isArray(devices) ? devices : [];
  const canon = ctx.canonicalGroupKey;
  return list.map((slot) => {
    const next = { ...slot };
    if (isSuper && !String(next.tenantOwner || "").trim() && typeof inferUniqueOwnerForGroup === "function") {
      const inferred = String(
        inferUniqueOwnerForGroup(devs, next.groupKey, canon) || "",
      ).trim();
      if (inferred) next.tenantOwner = inferred;
    }
    if (!String(next.groupDisplaySample || "").trim()) {
      for (const d of devs) {
        if (canon(d && d.notification_group) !== String(next.groupKey || "")) continue;
        const raw = String((d && d.notification_group) || "").trim();
        if (raw) {
          next.groupDisplaySample = raw;
          break;
        }
      }
    }
    return next;
  });
}

/**
 * Resolve human title for a group card (display meta > server sample > key).
 *
 * @param {{ groupKey?: string, groupDisplaySample?: string }} slot
 * @param {{ display_name?: string }} metaEntry
 * @param {(v: unknown) => string} displayGroupName
 * @returns {string}
 */
export function resolveOverviewGroupCardTitle(slot, metaEntry, displayGroupName) {
  const dn = String((metaEntry && metaEntry.display_name) || "").trim();
  if (dn) return dn;
  const sample = String((slot && slot.groupDisplaySample) || "").trim();
  if (sample) return displayGroupName(sample) || sample;
  const gk = String((slot && slot.groupKey) || "").trim();
  return displayGroupName(gk) || gk;
}

/**
 * Resolve ``owner_admin`` query for server group alarm/apply (matches API tenant infer).
 *
 * @param {{ groupKey: string, tenantOwner?: string, serverCounts?: { total?: unknown } }} slot
 * @param {object[]} devices
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @param {(devices: object[], gk: string, canonicalGroupKey: (v: unknown) => string) => string} inferUniqueOwnerForGroup
 * @returns {string}
 */
export function resolveGroupAlarmOwnerAdmin(slot, devices, ctx, inferUniqueOwnerForGroup) {
  const explicit = String(slot.tenantOwner || "").trim();
  if (explicit) return explicit;
  const me = ctx && ctx.me;
  if (!(me && me.role === "superadmin")) return "";
  const inferred = inferUniqueOwnerForGroup(
    Array.isArray(devices) ? devices : [],
    slot.groupKey,
    ctx.canonicalGroupKey,
  );
  return String(inferred || "").trim();
}

/**
 * @param {object | null | undefined} ov
 * @returns {boolean}
 */
export function hasUsableOverviewGroupSlots(ov) {
  return !!(ov && Array.isArray(ov.notification_group_slots));
}

/**
 * @param {Array<object>} out
 * @param {object} slot
 */
function upsertOverviewSlotByMetaKey(out, slot) {
  const mk = String(slot.metaKey || "");
  if (!mk) return;
  const idx = out.findIndex((s) => String(s.metaKey || "") === mk);
  if (idx < 0) {
    out.push(slot);
    return;
  }
  const prev = out[idx];
  const prevCounts = prev.serverCounts || {};
  const nextCounts = slot.serverCounts || {};
  prev.serverCounts = {
    total: Number(prevCounts.total || 0) + Number(nextCounts.total || 0),
    online: Number(prevCounts.online || 0) + Number(nextCounts.online || 0),
    offline: Math.max(
      0,
      Number(prevCounts.total || 0) + Number(nextCounts.total || 0)
        - (Number(prevCounts.online || 0) + Number(nextCounts.online || 0)),
    ),
  };
  const sample = String(slot.groupDisplaySample || "").trim();
  if (sample && (!prev.groupDisplaySample || sample.length < String(prev.groupDisplaySample).length)) {
    prev.groupDisplaySample = sample;
  }
}

/**
 * @param {object | null | undefined} ov
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string, groupCardMetaKey: (gk: string, tenant?: string) => string }} ctx
 * @returns {Array<object> | null} null → fallback to client device list slots
 */
export function buildOverviewSlotsFromApiResponse(ov, ctx) {
  if (!hasUsableOverviewGroupSlots(ov)) return null;
  const { canonicalGroupKey, groupCardMetaKey } = ctx;
  const out = [];
  for (const row of ov.notification_group_slots) {
    if (!row || typeof row !== "object") continue;
    const gk = canonicalGroupKey(String(row.group_key || ""));
    if (!gk) continue;
    const tenant = resolveOverviewSlotTenantOwner(row, ctx);
    const mk = groupCardMetaKey(gk, tenant);
    if (!mk) continue;
    const total = Number(row.device_count != null ? row.device_count : 0);
    const online = Number(row.online_count != null ? row.online_count : 0);
    upsertOverviewSlotByMetaKey(out, {
      metaKey: mk,
      groupKey: gk,
      tenantOwner: tenant,
      groupDisplaySample: String(row.group_display_sample || "").trim() || gk,
      serverCounts: {
        total,
        online,
        offline: Math.max(0, total - online),
      },
    });
  }
  out.sort((a, b) => {
    const c = a.groupKey.localeCompare(b.groupKey);
    return c !== 0 ? c : a.tenantOwner.localeCompare(b.tenantOwner);
  });
  return out;
}

/**
 * @param {Array<object>} slots
 * @param {string} ownerFilterQ
 * @returns {Array<object>}
 */
export function filterOverviewSlotsByOwner(slots, ownerFilterQ) {
  const list = Array.isArray(slots) ? slots : [];
  const q = String(ownerFilterQ || "").trim().toLowerCase();
  if (!q) return list;
  return list.filter((s) => String(s.tenantOwner || "").toLowerCase().includes(q));
}

/**
 * @param {Array<object>} slots
 * @param {Array<object>} settingsItems
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string, groupCardMetaKey: (gk: string, tenant?: string) => string }} ctx
 * @returns {Array<object>}
 */
export function mergeSettingsOnlyOverviewSlots(slots, settingsItems, ctx) {
  const { canonicalGroupKey, groupCardMetaKey } = ctx;
  const keys = new Set((Array.isArray(slots) ? slots : []).map((s) => String(s.metaKey || "")));
  const out = Array.isArray(slots) ? slots.slice() : [];
  const settings = Array.isArray(settingsItems) ? settingsItems : [];
  for (const x of settings) {
    const gk = canonicalGroupKey(String(x.group_key || ""));
    const tenant = resolveOverviewSlotTenantOwner(x, ctx);
    const mk = groupCardMetaKey(gk, tenant);
    if (!gk || !mk || keys.has(mk)) continue;
    const tenantNorm = String(tenant || "").trim();
    const ownerSettings = settings.filter(
      (row) => String(resolveOverviewSlotTenantOwner(row, ctx) || "").trim() === tenantNorm,
    );
    const serverHasDevices = out.some((s) => {
      if (String(s.tenantOwner || "").trim() !== tenantNorm) return false;
      return (_slotServerTotal(s) || 0) > 0;
    });
    if (ownerSettings.length === 1 && serverHasDevices) continue;
    keys.add(mk);
    out.push({
      metaKey: mk,
      groupKey: gk,
      tenantOwner: tenant,
      serverCounts: { total: 0, online: 0, offline: 0 },
    });
  }
  out.sort((a, b) => {
    const c = a.groupKey.localeCompare(b.groupKey);
    return c !== 0 ? c : a.tenantOwner.localeCompare(b.tenantOwner);
  });
  return out;
}

/**
 * @param {object | null | undefined} ov
 * @param {string} [bannerId]
 * @returns {string}
 */
export function overviewGroupSlotsTruncatedHtml(ov, bannerId = "ovGroupSlotsTruncBanner") {
  if (!ov || !ov.notification_group_slots_truncated) return "";
  const cap = ov.notification_group_slots_cap != null ? String(ov.notification_group_slots_cap) : "?";
  return (
    `<p class="badge offline" id="${bannerId}" data-group-slots-truncated="1" style="margin:8px 0">` +
    `Group directory capped at ${cap} server-side slots — some groups may be hidden.` +
    `</p>`
  );
}

/**
 * @param {ParentNode | null | undefined} view
 * @param {object | null | undefined} ov
 * @param {string} [bannerId]
 */
export function syncOverviewGroupSlotsTruncBanner(view, ov, bannerId = "ovGroupSlotsTruncBanner") {
  if (!view) return;
  const existing = view.querySelector(`#${bannerId}`);
  const html = overviewGroupSlotsTruncatedHtml(ov, bannerId);
  if (!html) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    if (existing.outerHTML !== html) existing.outerHTML = html;
    return;
  }
  const head = view.querySelector(".page-head--with-landing") || view.querySelector(".page-head") || view.firstElementChild;
  if (head && head.insertAdjacentHTML) {
    head.insertAdjacentHTML("afterend", html);
  } else if (view.firstElementChild) {
    view.firstElementChild.insertAdjacentHTML("beforebegin", html);
  }
}

/**
 * @param {object | null | undefined} ov
 * @param {object[]} [fleetDevices]
 * @returns {string[]}
 */
export function mergeTenantOwnerDirectory(ov, fleetDevices) {
  const merged = new Set();
  if (ov && Array.isArray(ov.tenant_owner_index)) {
    for (const u of ov.tenant_owner_index) {
      const t = String(u || "").trim();
      if (t) merged.add(t);
    }
  }
  if (merged.size) return [...merged].sort();
  const fleet = Array.isArray(fleetDevices) ? fleetDevices : [];
  for (const d of fleet) {
    const t = String(d.owner_admin || "").trim();
    if (t) merged.add(t);
  }
  return [...merged].sort();
}

/**
 * @param {object | null | undefined} ov
 * @param {string} [bannerId]
 * @returns {string}
 */
export function overviewTenantOwnerIndexTruncatedHtml(ov, bannerId = "ovTenantOwnerIndexTruncBanner") {
  if (!(ov && ov.tenant_owner_index_truncated)) return "";
  const cap = ov.tenant_owner_index_cap != null ? Number(ov.tenant_owner_index_cap) : "";
  return `<p class="badge offline" id="${bannerId}" data-tenant-owner-index-truncated="1" style="margin:0 0 8px">Account-owner list is truncated${cap ? ` (showing up to ${cap})` : ""} — search still works on loaded owners.</p>`;
}

/**
 * @param {object | null | undefined} ov
 * @param {number | null | undefined} override
 * @returns {number}
 */
export function resolveOverviewFleetMaxPages(ov, override) {
  if (Number.isFinite(Number(override))) return Math.max(0, Number(override));
  if (hasUsableOverviewGroupSlots(ov)) return 1;
  return undefined;
}
